import { Inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, of, from } from 'rxjs';
import { tap, switchMap, catchError } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { Label } from '@telespot/protocols/data-access';
import { LABEL_SERVICE, type LabelRepository } from '@telespot/protocols/domain';

export interface LabelState {
  labels: Label[];
  loading: boolean;
  error: string | undefined;
}

const initialState: LabelState = {
  labels: [],
  loading: true,
  error: undefined,
};

@Injectable()
export class LabelStore extends ComponentStore<LabelState> {
  constructor(@Inject(LABEL_SERVICE) private readonly labelRepo: LabelRepository) {
    super(initialState);
  }

  // #region Selectors

  public readonly labels$ = this.select((state) => state.labels);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly error$ = this.select((state) => state.error);

  public readonly labelIds$ = this.select((state) => state.labels.map((label) => label.id));

  // #endregion

  // #region Updaters

  public readonly setLabels = this.updater((state, labels: Label[]) => ({
    ...state,
    labels,
  }));

  public readonly addLabels = this.updater((state, labels: Label[]) => ({
    ...state,
    labels: [...state.labels, ...labels],
  }));

  public readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  public readonly setError = this.updater((state, error: string) => ({
    ...state,
    error,
  }));

  public readonly clearError = this.updater((state) => ({
    ...state,
    error: undefined,
  }));

  // #endregion

  // #region Effects

  public readonly fetchLabelsByUUID = this.effect((uuids$: Observable<string[]>) =>
    uuids$.pipe(
      tap(() => {
        this.setLoading(true);
        this.clearError();
      }),
      concatLatestFrom(() => this.labelIds$),
      switchMap(([uuids, existingLabelIds]) => {
        const missingLabelIds = uuids.filter((labelId) => !existingLabelIds.includes(labelId));

        if (!missingLabelIds.length) {
          this.setLoading(false);
          return of(null);
        }
        return from(this.labelRepo.fetchByUuid(missingLabelIds)).pipe(
          tap((labels: Label[]) => {
            this.addLabels(labels);
            this.setLoading(false);
            this.clearError();
          }),
          catchError((error: string) => {
            this.setError(error);
            this.setLoading(false);
            return of(null);
          })
        );
      })
    )
  );

  // #endregion
}
